import {metaInfo} from '@configs';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {
  getCategoryLabel,
  getConfigByApp,
  getIdBySlug,
  isUserAgentForBot,
  removedUndefinedFromArrayObject,
  utilServerSideDeviceDetection,
} from '@utils';
import cookie from 'cookie';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import loadAds from '@jobseeker/common-core/helpers/loadAds';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {JobDetail as JD} from '@jobseeker/common-core/types';

import {
  COOKIE_LOGIN_AUTH,
  HIRIST_CATEGORY_BLOG_ID_MAPPING,
  HIRIST_CATEGORY_ID_MAPPING,
  JOBFEED_BANNER_COOKIE,
} from '@jobseeker/common-core/constants';
import {
  dataService,
  jobDetailService,
  jobfeedService,
  userService,
} from '@jobseeker/common-core/services';
import {AppState, wrapper} from '@jobseeker/common-core/stores';

import {VersionVariants} from '@jobseeker/lotus/enums';

import {Chat} from '@components';
import {
  BOTTOM_SHEET_COPIES,
  JOBDETAIL,
  JOBFEED,
  NUDGE_ALLOWED_REFS,
} from '@constants';
import {loadSEO} from '@helpers';
import {getCookiesToDelete, setSSRCookies} from '@helpers/ssrMap';
import jobFeedService from '@jobseeker/common-core/services/jobfeed.service';
import {getExperienceIndex, openUrl} from '@jobseeker/common-core/utils/common';
import {getDetailSEOContent} from '@utils/seo';

import HiristJobDetail from '@components/JobDetail';
import type {JobDetail as JB} from '@jobseeker/common-core/stateModels/job/jobdetail.models';
import {AdsElementType} from '@jobseeker/common-core/types';
import {TrackEventUBA, getFormatedProfile} from '@jobseeker/common-core/utils';
import {cleanUrl, getFilteredCategoryList} from '@jobseeker/lotus/utils';
import type {GetServerSidePropsContext} from 'next/types';
import {useRouter} from 'next/router';
import {useEffect, useMemo, useRef, useState} from 'react';

import {gladiatorService} from '@jobseeker/common-core/services';
import NotFoundPage from '../404';
import JobDetail from '@layouts/Body/Jobdetail';
import {Box, Card, Grid, Skeleton, Typography} from '@mui/material';
import {
  getAppendedLinkForBreadcrumb,
  getBreadcrumbsElements,
} from '@layouts/ResponsiveLayout';
import {BreadcrumbJsonLd} from 'next-seo';
import {Breadcrumbs} from '@jobseeker/lotus/components';
import {BottomSheet} from '@jobseeker/lotus/elements';
import {
  CrossOutlined,
  IimjobsLogoOutlined,
} from '@jobseeker/lotus/assets/icons';
import Image from 'next/image';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {BrandedJDData} from '@jobseeker/lotus/components';

const PLATFORM = process?.env?.NEXT_PUBLIC_APP_NAME ?? '';
const isAppHirist = PLATFORM === 'hirist';

type JobDetailPageProps = {
  userIdSSR: number;
  jobId: string;
  jobMandateInfo: {
    jobTitle: string;
    jobCategory: number;
  };
  brandedJD?: number;
  brandedS3?: string;
  metaData: /* eslint-disable @typescript-eslint/no-explicit-any */ any;
  seoConfig: object;
  refInfo?: string;
  isMobile?: boolean;
  categoryName?: string;
  show410?: boolean; // <-- add this prop
  showFreeProBannerToday?: boolean;
};
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const JobDetailPage = ({
  userIdSSR,
  jobId,
  jobMandateInfo,
  metaData,
  seoConfig,
  refInfo,
  brandedJD,
  brandedS3,
  isMobile,
  categoryName = '',
  show410 = false, // <-- default value
  showFreeProBannerToday,
}: JobDetailPageProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  dispatch({
    type: 'AppContext/set_recap',
    payload: jobId,
  });
  const detail: JB = useSelector((state: AppState) => state.jobDetail);
  const [brandedJDS3, setBrandedJDS3] = useState(brandedJD || 0);

  const category = getCategoryLabel(
    metaData?.header?.meta?.tabsList,
    jobMandateInfo?.jobCategory || detail?.categoryId
  );
  const [brandedJDData, setbrandedJDData] = useState<BrandedJDData | any>({});
  const [isBrandedDataLoaded, setIsBrandedDataLoaded] = useState(false);
  const [jobDetailsLoading, setJobDetailsLoading] = useState(!detail?.id);
  const [localShow410, setLocalShow410] = useState(show410);
  const [dynamicSeoConfig, setDynamicSeoConfig] = useState<any>(null);
  const [localCategoryName, setLocalCategoryName] = useState(categoryName);
  const [shouldShowBottomSheet, setShouldShowBottomSheet] = useState(true);
  const [copy, setCopy] = useState(BOTTOM_SHEET_COPIES[0]);

  const getBrandedData = async (bJD: number, bS3: string) => {
    try {
      if (bJD !== 1 || !bS3) {
        setIsBrandedDataLoaded(true);
        return;
      }
      const response = await dataService.getBrandeddata(bS3);
      setbrandedJDData(response.data);
    } catch (error) {
      console.error('Error fetching JSON:', error);
      setBrandedJDS3(0);
    } finally {
      setIsBrandedDataLoaded(true);
    }
  };

  useEffect(() => {
    getBrandedData(brandedJD || 0, brandedS3 || '');
  }, []);

  useEffect(() => {
    if (!jobDetailsLoading) return;
    const fetchJobDetails = async () => {
      let isRedirecting = false;
      try {
        const jobDetails: any = await jobDetailService.getJobDetails({jobId});
        if (jobDetails?.refJobId) {
          isRedirecting = true;
          const [pathname, search] = router.asPath.split('?');
          const newPathname = pathname.replace(
            new RegExp(`([-/])${jobId}$`),
            `$1${jobDetails.refJobId}`
          );
          const newPath = search ? `${newPathname}?${search}` : newPathname;
          window.location.replace(newPath);
          return;
        }
        if (
          (jobDetails?.permanentlyRemoved || jobDetails?.state === 2) &&
          !jobDetails?.applied
        ) {
          setLocalShow410(true);
          return;
        }
        if (jobDetails?.hasExpired && !jobDetails?.applied) {
          router.replace('/');
          return;
        }
        dispatch({type: 'job/jobdetail/set_jobdetail', payload: jobDetails});
        if (isAppHirist) {
          const tabsList = metaData?.header?.meta?.tabsList || [];
          const catInfo = getFilteredCategoryList(tabsList).find(
            (cat: any) => cat.dataId == jobDetails?.categoryId
          );
          setLocalCategoryName(catInfo?.label || '');
        }
        const bJD = jobDetails?.brandedJd || 0;
        const bS3 = jobDetails?.brandedS3 || '';
        setBrandedJDS3(bJD);
        if (bJD === 1 && bS3) {
          getBrandedData(bJD, bS3);
        }
        const appDomain = process.env.NEXT_PUBLIC_APP_DOMAIN || '';
        setDynamicSeoConfig({
          title: `${jobDetails?.title || ''} - ${
            jobDetails?.companyData?.companyName || ''
          } | ${appDomain}`,
          noindex: !jobDetails?.active,
          nofollow: !jobDetails?.active,
        });
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setLocalShow410(true);
        } else {
          console.error('CSR job detail fetch error:', err);
        }
      } finally {
        if (!isRedirecting) {
          setJobDetailsLoading(false);
        }
      }
    };
    fetchJobDetails();
  }, []);

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: isAppHirist ? localCategoryName : category?.label,
      item: isAppHirist
        ? `/c/${cleanUrl(localCategoryName)}-jobs?&ref=breadcrumb_jd`
        : category?.tag
        ? `/c/${category?.tag
            .replace('&', '')
            .replace(/\s+/g, '-')
            .toLowerCase()}-jobs?&ref=breadcrumb_jd`
        : '',
    },
    {
      name: (jobMandateInfo?.jobTitle || detail?.title)
        ?.split(',')[0]
        ?.split('(')[0]
        ?.trim(),
      item: '',
    },
  ];

  const router = useRouter();
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);
  const {tabsList} = metaData?.header?.meta || {};

  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const bottomSheetCheck =
    !isAppHirist &&
    device === 'mobile' &&
    NUDGE_ALLOWED_REFS.includes(router.query.ref as string) &&
    userId &&
    userId % 2 === 0;

  const [jobs, setJobs] = useState<JD[]>([]);
  useEffect(() => {
    const fetchCompanyJobs = async () => {
      const data = await jobfeedService.getSimilarJobs({
        userId,
        id: detail.id,
        cat: detail.categoryId,
        tags: detail.tags?.map((x: {id: number}) => x.id?.toString()) ?? '',
        exp: getExperienceIndex(detail?.min) || undefined,
        functionalArea: detail?.functionalArea,
        min: detail?.min,
        size: 8,
        companyIds: detail?.recruiter?.centralizedCompanyId.toString() || '',
        includeAppliedJobs: 1,
        ...(isAppHirist && {whatFor: 'hirist'}),
      });
      setJobs(data?.data || []);
    };
    if (brandedJDS3 == 1 && detail?.id) {
      fetchCompanyJobs();
    }
  }, [brandedJDS3, detail?.id]);

  const handleBottomSheetContinueClick = () => {
    TrackEventUBA('appDownloadLinkageClick', 'click', {
      ref: router.query.ref || '',
      pageName: 'Job Description Page',
      linkageName: 'Install App Bottomsheet on JD from Deeplink',
      linkageType: 'bottomsheetBanner',
      typeClick: 'CTA',
      page: 'JD',
      sessionUsername: sessionStorage.getItem('userId') || '',
      copyType: BOTTOM_SHEET_COPIES.indexOf(copy) + 1,
    });
    openUrl('https://iimjobs.onelink.me/TyLF/vxhhhrl6');
    setShouldShowBottomSheet(false);
  };

  const checkBottomSheetEligibility = () => {
    try {
      const cookieName = 'app_download_bottom_sheet';
      const cookieExists = document.cookie
        .split(';')
        .some(cookie => cookie.trim().startsWith(`${cookieName}=`));

      if (!cookieExists) {
        const expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + 24 * 60 * 60 * 1000); // 24 hours

        document.cookie = `${cookieName}=true; expires=${expirationDate.toUTCString()}; path=/`;

        return true;
      }

      return false;
    } catch (error) {
      console.warn('Cookie not available:', error);
      return true;
    }
  };

  useEffect(() => {
    if (!bottomSheetCheck) return;
    const canShow = checkBottomSheetEligibility();
    setShouldShowBottomSheet(canShow);

    if (canShow) {
      const randomIndex = Math.floor(
        Math.random() * BOTTOM_SHEET_COPIES.length
      );
      setCopy(BOTTOM_SHEET_COPIES[randomIndex]);
      TrackEventUBA('appDownloadLinkageView', 'view', {
        ref: router.query.ref || '',
        pageName: 'Job Description Page',
        linkageName: 'Install App Bottomsheet on JD from Deeplink',
        linkageType: 'bottomsheetBanner',
        page: 'JD',
        sessionUsername: sessionStorage.getItem('userId') || '',
        copyType: randomIndex + 1,
      });
    }
  }, [router.query.ref, userId]);

  const handleBottomSheetDismiss = () => {
    setShouldShowBottomSheet(false);
    TrackEventUBA('appDownloadLinkageClick', 'click', {
      ref: router.query.ref || '',
      pageName: 'Job Description Page',
      linkageName: 'Install App Bottomsheet on JD from Deeplink',
      linkageType: 'bottomsheetBanner',
      typeClick: 'Close',
      page: 'JD',
      sessionUsername: sessionStorage.getItem('userId') || '',
      copyType: BOTTOM_SHEET_COPIES.indexOf(copy) + 1,
    });
  };

  const breadCrumbs = getBreadcrumbsElements(
    breadcrumbElements,
    isAppHirist,
    device
  );
  const JobCategory = useMemo(() => {
    const tags = [];
    const categoryId = detail.categoryId;
    for (let i = 0; i < tabsList.length; i++) {
      if (tabsList[i]?.dataId == categoryId) {
        return tabsList[i];
      }
      tags.push(...tabsList[i].menu);
    }

    for (let j = 0; j < tags.length; j++) {
      if (tags[j].dataId == categoryId) {
        return tags[j];
      }
    }
  }, [detail.categoryId]);
  const hasTracked = useRef(false);

  const isBranded = brandedJDS3 === 1;

  useEffect(() => {
    if (hasTracked.current) return;

    const sessionUserId = userIdSSR || userId || '-1';

    if (!detail?.id) return;
    if (!isBrandedDataLoaded) return;
    let visibleKeysString = Object.entries(brandedJDData)
      .filter(([, value]: [string, any]) => value?.visibility === true)
      .map(([key, value]: [string, any]) => {
        if (key === 'banner') {
          if (value?.preferenceKey === 'bannerVideo') {
            return 'Video Banner';
          } else return 'Image Banner';
        } else if (key === 'aboutCompany') return 'AboutCompany';
        else if (key === 'awardsRecognitions') return 'Awards';
        else if (key === 'benefits') return 'Benefits';
        else if (key === 'keyHighlights') return 'KeyHighlights';
        else if (key === 'reviews') return 'Reviews';
      })
      .join(',');
    if (jobs && jobs?.length > 0) {
      if (visibleKeysString.length > 0) {
        visibleKeysString += ',';
      }
      visibleKeysString += 'SimilarJobs';
    }
    hasTracked.current = true;
    TrackEventUBA('jobDescriptionPageView', 'view', {
      ref: router.query.ref || '',
      jobID: detail.id,
      jobName: detail.title,
      jobpos: router.query.jobPos ?? '',
      categoryID: detail.categoryId,
      categoryName: JobCategory?.label,
      pageName: 'JobDescriptionPage',
      sessionUsername: sessionUserId,
      isBranded: isBranded ? 'Yes' : 'No',
      templateID: isBranded
        ? brandedJDData?.templateInfo?.templateNo || ''
        : '',
      section: isBranded ? visibleKeysString : '',
      companyID: detail?.companyData?.companyId,
      centralCompanyID: detail?.recruiter?.centralizedCompanyId,
      companyName: detail?.companyData?.companyName,
      recruiterActionStatus: detail?.recruiterActionText || '',
    });

    if (
      isBranded &&
      brandedJDData?.banner &&
      brandedJDData?.banner?.visibility
    ) {
      TrackEventUBA('sectionView', 'view', {
        ref: router.query.ref || '',
        pageName: 'JD',
        jobID: detail.id,
        page: 'JD',
        templateID: brandedJDData?.templateInfo?.templateNo,
        isBranded: 'Yes',
        companyID: detail?.companyData?.companyId,
        centralCompanyID: detail?.recruiter?.centralizedCompanyId,
        companyName: detail?.companyData?.companyName,
        section: `${
          brandedJDData?.banner?.preferenceKey === 'bannerVideo'
            ? 'Video'
            : 'Image'
        } Banner`,
        sessionUsername: sessionStorage.getItem('userId') || '',
      });
    }
  }, [userId, detail?.id, isBrandedDataLoaded]);

  if (localShow410) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Interceptor metaInfo={metaData}>
        {(dynamicSeoConfig || Object.keys(seoConfig as object).length > 0) && (
          <SeoLayout
            seoConfig={{...(seoConfig as object), ...(dynamicSeoConfig || {})}}
          />
        )}
        <NavLayout isMobile={isMobile}>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={
              !isBranded || isAppHirist ? breadcrumbElements : []
            }
          >
            {jobDetailsLoading ? (
              isAppHirist ? (
                <>
                  {/* Banner */}
                  <Box
                    sx={{
                      bgcolor: '#13355c',
                      backgroundImage:
                        "url('https://job-static.hirist.com/V8/static/media/background-bg.ba8365c7.webp')",
                      backgroundRepeat: 'no-repeat',
                      px: {xxs: 2, md: 6},
                      pb: {xxs: 2, md: 9},
                      pt: {xxs: 4.5, md: 7},
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: {xxs: 1.5, md: 3},
                    }}
                  >
                    <Skeleton
                      variant="rounded"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        flexShrink: 0,
                        borderRadius: 2,
                        width: {xxs: 48, md: 90},
                        height: {xxs: 48, md: 90},
                      }}
                    />
                    <Box flex={1}>
                      <Skeleton
                        width="65%"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          mb: 0.5,
                          height: {xxs: 22, md: 30},
                        }}
                      />
                      <Skeleton
                        width="40%"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          mb: 1,
                          height: {xxs: 15, md: 18},
                        }}
                      />
                      <Box display="flex" gap={0.75} flexWrap="wrap" mb={1.25}>
                        {[90, 105, 65, 55, 120, 75, 100].map((w, i) => (
                          <Skeleton
                            key={i}
                            variant="rounded"
                            width={w}
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.15)',
                              borderRadius: '20px',
                              height: {xxs: 20, md: 26},
                            }}
                          />
                        ))}
                      </Box>
                      <Skeleton
                        width="30%"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.12)',
                          height: {xxs: 12, md: 14},
                        }}
                      />
                    </Box>
                  </Box>
                  {/* Content area */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: {xxs: 'column', md: 'row'},
                      gap: {xxs: 2, md: 4},
                      px: {xxs: 0, md: 6},
                      mt: 3,
                    }}
                  >
                    <Box sx={{width: {xxs: '100%', md: '62%', lg: '68%'}}}>
                      <Typography
                        component="h2"
                        mt={0}
                        mb={1.25}
                        fontSize={22}
                        fontWeight={700}
                      >
                        Job Description
                      </Typography>
                      <Card sx={{borderRadius: 2, p: 2.5}}>
                        {[
                          100, 100, 65, 100, 100, 50, 100, 100, 75, 100, 100,
                          60, 100, 100, 80, 100, 55, 100, 100, 70, 100, 100, 45,
                          100, 100, 85, 100,
                        ].map((w, i) => (
                          <Skeleton
                            key={i}
                            height={15}
                            width={`${w}%`}
                            sx={{mb: 0.75}}
                          />
                        ))}
                      </Card>
                    </Box>
                    <Box sx={{flex: 1}}>
                      <Card sx={{borderRadius: 2, p: 2, mb: 2}}>
                        <Skeleton height={13} width="40%" sx={{mb: 1.5}} />
                        <Box
                          display="flex"
                          gap={1.5}
                          alignItems="center"
                          mb={1.5}
                        >
                          <Skeleton
                            variant="circular"
                            width={38}
                            height={38}
                            sx={{flexShrink: 0}}
                          />
                          <Box flex={1}>
                            <Skeleton height={15} width="60%" sx={{mb: 0.4}} />
                            <Skeleton height={13} width="75%" sx={{mb: 0.3}} />
                            <Skeleton height={11} width="85%" />
                          </Box>
                        </Box>
                        <Box
                          display="flex"
                          justifyContent="space-around"
                          mb={1.5}
                        >
                          {[0, 1, 2].map(i => (
                            <Box key={i} textAlign="center">
                              <Skeleton
                                height={22}
                                width={36}
                                sx={{mx: 'auto', mb: 0.4}}
                              />
                              <Skeleton height={11} width={55} />
                            </Box>
                          ))}
                        </Box>
                        <Skeleton height={13} width="45%" sx={{mb: 0.3}} />
                        <Skeleton height={14} width="65%" sx={{mb: 1.25}} />
                        <Skeleton height={13} width="50%" sx={{mb: 0.3}} />
                        <Skeleton height={14} width="70%" sx={{mb: 1.25}} />
                        <Skeleton height={13} width="35%" sx={{mb: 0.3}} />
                        <Skeleton height={14} width="30%" />
                      </Card>
                      <Card sx={{borderRadius: 2, p: 2}}>
                        <Skeleton height={13} width="55%" sx={{mb: 1.5}} />
                        <Skeleton
                          variant="rounded"
                          height={160}
                          sx={{borderRadius: 1}}
                        />
                      </Card>
                    </Box>
                  </Box>
                </>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xxs={12} md={8.5} lg={8.8}>
                    <Card sx={{borderRadius: 4, p: 2}}>
                      <Box display="flex" gap={2} alignItems="flex-start">
                        <Skeleton
                          variant="rounded"
                          width={50}
                          height={50}
                          sx={{borderRadius: 1, flexShrink: 0}}
                        />
                        <Box flex={1}>
                          <Skeleton height={26} width="72%" sx={{mb: 0.5}} />
                          <Skeleton height={18} width="48%" sx={{mb: 0.25}} />
                          <Skeleton height={16} width="32%" />
                        </Box>
                      </Box>
                      <Box display="flex" gap={0.75} flexWrap="wrap" mt={1.5}>
                        {[70, 100, 60, 80, 65, 85, 55, 75].map((w, i) => (
                          <Skeleton
                            key={i}
                            variant="rounded"
                            height={16}
                            width={w}
                            sx={{borderRadius: 1}}
                          />
                        ))}
                      </Box>
                      <Box display="flex" gap={2} mt={2}>
                        <Skeleton
                          variant="rounded"
                          height={16}
                          width={100}
                          sx={{borderRadius: 1}}
                        />
                        <Skeleton
                          variant="rounded"
                          height={16}
                          width={80}
                          sx={{borderRadius: 1}}
                        />
                      </Box>
                    </Card>
                    <Card sx={{borderRadius: 4, mt: 2, p: 2.5}}>
                      {[
                        100, 100, 70, 100, 100, 55, 100, 100, 80, 100, 100, 65,
                        100, 90, 100, 100, 60, 100, 100, 75, 100, 100, 50, 100,
                        100, 85, 100, 70, 100, 100, 55, 100, 100, 80, 100, 65,
                        100, 90, 100, 100,
                      ].map((w, i) => (
                        <Skeleton
                          key={i}
                          height={15}
                          width={`${w}%`}
                          sx={{mb: 0.75}}
                        />
                      ))}
                    </Card>
                  </Grid>
                  <Grid item xxs={12} md={3.5} lg={3.2}>
                    {/* Posted by card */}
                    <Card sx={{borderRadius: 2, p: 2, mb: 2}}>
                      <Skeleton height={14} width="40%" sx={{mb: 1.5}} />
                      <Box
                        display="flex"
                        gap={1.5}
                        alignItems="center"
                        mb={1.5}
                      >
                        <Skeleton
                          variant="circular"
                          width={40}
                          height={40}
                          sx={{flexShrink: 0}}
                        />
                        <Box flex={1}>
                          <Skeleton height={16} width="70%" sx={{mb: 0.5}} />
                          <Skeleton height={13} width="85%" sx={{mb: 0.25}} />
                          <Skeleton height={12} width="50%" />
                        </Box>
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-around"
                        mb={1.5}
                      >
                        {[0, 1, 2].map(i => (
                          <Box key={i} textAlign="center">
                            <Skeleton
                              height={24}
                              width={32}
                              sx={{mx: 'auto', mb: 0.5}}
                            />
                            <Skeleton height={11} width={60} />
                          </Box>
                        ))}
                      </Box>
                      <Skeleton height={36} sx={{borderRadius: 1, mb: 1.5}} />
                      <Skeleton height={14} width="55%" sx={{mb: 0.25}} />
                      <Skeleton height={13} width="40%" sx={{mb: 1.5}} />
                      <Skeleton height={14} width="45%" sx={{mb: 0.25}} />
                      <Skeleton height={13} width="30%" />
                    </Card>
                    {/* Featured institute / ad card */}
                    <Card sx={{borderRadius: 2, p: 2}}>
                      <Skeleton height={14} width="55%" sx={{mb: 1.5}} />
                      <Skeleton
                        variant="rounded"
                        height={180}
                        sx={{borderRadius: 1}}
                      />
                    </Card>
                  </Grid>
                </Grid>
              )
            ) : isAppHirist ? (
              <HiristJobDetail
                jobId={jobId}
                refInfo={refInfo}
                categoryName={localCategoryName}
                isMobile={isMobile}
                brandedData={brandedJDData}
                brandedJD={brandedJDS3}
                similarJobs={jobs}
                userIdSSR={userIdSSR}
              />
            ) : (
              <JobDetail
                dispatch={dispatch}
                showFeatured={false}
                showLearning={false}
                jobId={jobId}
                refInfo={refInfo}
                brandedData={brandedJDData}
                brandedJD={brandedJDS3}
                similarJobs={jobs}
                userIdSSR={userIdSSR}
                showFreeProBannerToday={showFreeProBannerToday}
              />
            )}
            {bottomSheetCheck && (
              <BottomSheet
                header={
                  <Box p={2}>
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      paddingLeft={1}
                    >
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        width={'100%'}
                        height={'40px'}
                      >
                        <Typography variant="subtitle1">
                          Switch to the iimjobs App
                        </Typography>
                      </Box>
                      <Image
                        src={CrossOutlined}
                        alt={'Close Icon'}
                        width={18}
                        height={18}
                        onClick={() => {
                          handleBottomSheetDismiss();
                        }}
                        objectFit="cover"
                        style={{cursor: 'pointer'}}
                      />
                    </Box>
                  </Box>
                }
                onDismiss={handleBottomSheetDismiss}
                open={shouldShowBottomSheet}
                bannerMode={false}
                snapPoints={() => [320]}
              >
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  alignItems={'center'}
                  gap={2}
                  marginTop={3}
                >
                  <Image
                    src={IimjobsLogoOutlined}
                    alt=""
                    width={54}
                    height={54}
                  />
                  <Box display={'flex'} flexDirection={'column'} gap={1}>
                    <Typography variant="subtitle1" align="center">
                      {copy.heading}
                    </Typography>
                    <Typography
                      variant="body2"
                      align="center"
                      mx={2}
                      color={'#727272'}
                    >
                      {copy.subHeading}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{width: '90%'}}
                    onClick={() => {
                      handleBottomSheetContinueClick();
                    }}
                  >
                    {copy.CTA}
                  </Button>
                </Box>
              </BottomSheet>
            )}
            {isBranded && !isAppHirist && (
              <Box
                sx={{
                  mb: -11,
                  mt: 7,
                  display: {xxs: 'none', md: 'block'},
                }}
              >
                {breadcrumbElements?.length > 0 && (
                  <BreadcrumbJsonLd
                    itemListElements={getAppendedLinkForBreadcrumb(
                      breadcrumbElements
                    )}
                  />
                )}
                {breadcrumbElements?.length > 0 && (
                  <Box
                    sx={
                      !isAppHirist
                        ? {pb: 2}
                        : {
                            borderBottom: '1px solid #f2f2f2',
                            borderTop: '1px solid #e6e6e6',
                            py: '9px',
                            pr: '50px',
                            pl: 2,
                            height: '34px',
                            width: '100%',
                            maxWidth: '1440px',
                            zIndex: 3,
                            // position: 'fixed',
                            background: '#fff',
                            boxShadow: '0 4px 15px 0 rgba(0,0,0,.06)',
                          }
                    }
                  >
                    <Breadcrumbs
                      variant={VersionVariants.VERSION_TWO}
                      separator={
                        <Typography
                          fontSize={10}
                          color={!isAppHirist ? 'secondary.main' : '#898989'}
                          mx={!isAppHirist ? 0 : 1.25}
                        >
                          {'>'}
                        </Typography>
                      }
                      breadcrumbs={breadCrumbs}
                    />
                  </Box>
                )}
              </Box>
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const jobSlug = context.params.jobslug;
    const applyDialogue = context.query.apply;
    const refInfo = context.query.ref || '';
    const jobSlugUrl = context?.resolvedUrl || '';
    const metaData = getConfigByApp(metaInfo, JOBFEED, true, JOBDETAIL);

    const jobId: string = jobSlug.split('-')?.pop() || '';

    let userId = -1;
    let shouldClearAuth = false;

    let isCohortEligible = false;
    const cookies = cookie.parse(context.req?.headers?.cookie || '');
    const showFreeProBannerToday = isAppIimjobs
      ? !cookies[JOBFEED_BANNER_COOKIE]
      : false;
    if (showFreeProBannerToday) {
      const existing = context.res.getHeader('Set-Cookie');
      const bannerCookie = `${JOBFEED_BANNER_COOKIE}=1; Max-Age=86400; Path=/; HttpOnly; SameSite=Lax`;
      if (Array.isArray(existing)) {
        context.res.setHeader('Set-Cookie', [...existing, bannerCookie]);
      } else if (existing) {
        context.res.setHeader('Set-Cookie', [existing as string, bannerCookie]);
      } else {
        context.res.setHeader('Set-Cookie', bannerCookie);
      }
    }

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (isAppIimjobs) {
          const eligibleData = await userService.cohortEligible(
            'free-pro-cohort',
            {
              headers: {
                cookie: context.req?.headers?.cookie,
              },
            }
          );
          if (eligibleData?.eligible) {
            isCohortEligible = true;
          }
        }

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          // Handle logout/refresh response from validation
          if (
            validateInfo?.type &&
            ['refresh', 'logout'].includes(validateInfo.type)
          ) {
            if (validateInfo.type === 'refresh' && validateInfo.token) {
              console.log('🚀 ~ Authentication validation returned refresh');
              const {ssrLastActCookie, ssrTokenCookie} = setSSRCookies(
                validateInfo.token
              );

              context.res?.setHeader('Set-Cookie', [
                ssrTokenCookie,
                ssrLastActCookie,
              ]);

              // Update request headers with refreshed cookies for subsequent API calls
              if (context.req?.headers) {
                context.req.headers.cookie = `${ssrTokenCookie}; ${ssrLastActCookie}`;
              }

              // Use the refreshed token for subsequent calls
              userId = parseInt(validateInfo?.userId ?? '0', 10);
              if (userId > 0) {
                store.dispatch({type: 'Auth/set_user_loggedin', payload: 1});
                store.dispatch({
                  type: 'Auth/set_user_info',
                  payload: {userId: String(userId)},
                });
              }
            }

            if (validateInfo.type === 'logout') {
              console.log('🚀 ~ Authentication validation returned logout');
              shouldClearAuth = true;
              userId = -1;

              // Clear auth state in store
              store.dispatch({
                type: 'Auth/set_user_id',
                payload: -1,
              });
              store.dispatch({
                type: 'Auth/set_pro',
                payload: false,
              });
            }
          } else {
            userId = parseInt(validateInfo?.userId ?? '0', 10);
            if (userId > 0) {
              store.dispatch({type: 'Auth/set_user_loggedin', payload: 1});
              store.dispatch({
                type: 'Auth/set_user_info',
                payload: {userId: String(userId)},
              });
            }
          }
        }
      }
    } catch (err) {
      // Handle any unexpected errors
      console.log('🚀 ~ Authentication validation error:', err);
      shouldClearAuth = true;
      userId = -1;
    }

    const token = context.query?.jsAuth;
    if (token && userId == -1) {
      try {
        const authInfo = await gladiatorService.refresh('refresh?type=ext', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (authInfo?.error || !authInfo?.token) {
          throw new Error('Invalid token');
        }

        const {ssrLastActCookie, ssrTokenCookie} = setSSRCookies(
          authInfo.token
        );
        context.res?.setHeader('Set-Cookie', [
          ssrTokenCookie,
          ssrLastActCookie,
        ]);

        // Update request headers with refreshed cookies for subsequent API calls
        if (context.req?.headers) {
          context.req.headers.cookie = `${ssrTokenCookie}; ${ssrLastActCookie}`;
        }

        store.dispatch({type: 'Auth/set_user_loggedin', payload: 1});
      } catch (error) {
        console.log('🚀 ~ Authentication validation error:', error);
      }
    }
    if (token) {
      const [pathName = '', search = ''] = jobSlugUrl.split('?');
      const searchParams = new URLSearchParams(search);
      searchParams.delete('jsAuth');
      const destination = searchParams.toString()
        ? `${pathName}?${searchParams.toString()}`
        : pathName;

      return {
        redirect: {
          permanent: false,
          destination,
        },
        props: {},
      };
    }

    const {
      header: {
        meta: {tabsList},
      },
      ads: {learnGrow, featuredInstitutes},
    } = metaData || {};

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: JOBDETAIL},
    });

    store.dispatch({
      type: 'AppContext/set_jd_apply',
      payload: /true/.test(applyDialogue),
    });

    if (isAppHirist) {
      store.dispatch({type: 'Auth/set_pro', payload: true});
    }

    if (typeof window === 'undefined') {
      store.dispatch({
        type: 'AppContext/set_enable_ads_preload',
        payload: true,
      });
    }

    store.dispatch({type: 'job/jobdetail/set_initial_state'});

    const jobMandateInfo = {
      id: 0,
      jobTitle: '',
      jobCategory: '',
      jobTags: '',
      functionalArea: '',
      brandedJD: 0,
      brandedUrl: '',

      expMin: 0,
      companyName: '',
      location: '',
      exp: '',
      confidential: 0,
    };

    const {isMobile} = utilServerSideDeviceDetection(context);
    console.log('🚀 ~ store=> ~ JD --> isMobile:', isMobile);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const isBotUser = isUserAgentForBot(context);

    if (!isBotUser) {
      return {
        props: {
          userIdSSR: userId,
          jobId,
          jobMandateInfo,
          metaData,
          seoConfig: {},
          refInfo,
          isMobile,
          categoryName: '',
          brandedJD: 0,
          brandedS3: '',
          showFreeProBannerToday: showFreeProBannerToday && isCohortEligible,
        },
      };
    }

    let categoryName = '';
    let jobDetails: JB | any = {};

    try {
      const userIp =
        context.req?.headers?.['x-forwarded-for']
          ?.toString()
          .split(',')[0]
          .trim() ||
        context.req?.socket?.remoteAddress ||
        '';
      let headers: Record<string, any> = {
        'user-agent': context.req?.headers?.['user-agent'],
        'x-forwarded-for': userIp,
        referer: `https://www.${process.env.NEXT_PUBLIC_APP_DOMAIN}/`,
      };

      if (context.req?.headers?.cookie && !shouldClearAuth) {
        const cookies = cookie.parse(context.req.headers.cookie);
        console.log(
          '🚀 ~ file: index.tsx:40 ~ store=> ~ cookies:',
          context.req?.headers
        );
        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          headers = {
            ...headers,
            cookie: context.req?.headers?.cookie,
            'user-agent': context.req?.headers?.['user-agent'],
          };
        }
      }

      const [jobDetailsResult, profileResponse] = await Promise.all([
        jobDetailService.getJobDetails({jobId, headers}),
        userId > 0
          ? userService.profile('profile?apply=true&cohort=true', headers)
          : Promise.resolve(null),
      ]);
      jobDetails = jobDetailsResult;

      if (profileResponse?.profile) {
        const formattedProfile = getFormatedProfile(profileResponse.profile);
        store.dispatch({
          type: 'User/set_user_profile',
          payload: formattedProfile.profile,
        });
        store.dispatch({
          type: 'User/set_profile_completeness',
          payload: formattedProfile.completeness,
        });
      }

      jobMandateInfo.brandedJD = jobDetails?.brandedJd || 0;
      jobMandateInfo.brandedUrl = jobDetails?.brandedS3 || '';
      console.log({xyzz: jobDetails?.brandedS3});

      if (jobDetails?.refJobId) {
        const jobUrl = `${jobSlugUrl?.replace(
          `-${jobId}`,
          `-${jobDetails?.refJobId}`
        )}`;

        return {
          redirect: {
            permanent: true,
            destination: refInfo ? `${jobUrl}?ref=${refInfo}` : jobUrl,
          },
          props: {},
        };
      }

      store.dispatch({
        type: 'job/jobdetail/set_jobdetail',
        payload: jobDetails,
      });

      jobMandateInfo.id = jobDetails?.id;
      jobMandateInfo.jobTags =
        jobDetails?.tags?.map((x: {id: number}) => x.id?.toString()) ?? '';
      jobMandateInfo.expMin = jobDetails?.min ?? 0;
      jobMandateInfo.jobTitle = jobDetails?.title;
      jobMandateInfo.jobCategory = jobDetails?.categoryId;
      jobMandateInfo.confidential = jobDetails?.confidential ?? 0;
      jobMandateInfo.functionalArea = jobDetails?.functionalArea ?? 0;

      if (jobDetails?.categoryId > -1) {
        let categoryInfo: /* eslint-disable @typescript-eslint/no-explicit-any */ any =
          {};

        if (isAppHirist) {
          const categoryMappedList = getFilteredCategoryList(tabsList);

          categoryInfo = categoryMappedList.find(
            cat => cat.dataId == jobDetails?.categoryId
          );

          categoryName = categoryInfo?.label;
        } else {
          const categoryId = jobDetails.categoryId;

          const cat = (() => {
            const tags = [];

            for (let i = 0; i < tabsList.length; i++) {
              if (tabsList[i]?.dataId == categoryId) {
                return tabsList[i];
              }
              tags.push(...tabsList[i].menu);
            }

            for (let j = 0; j < tags.length; j++) {
              if (tags[j].dataId == categoryId) {
                return tags[j];
              }
            }

            return null;
          })();
          categoryName = cat?.tag
            ? `${cat.tag.toLowerCase().replace(/[^A-Z0-9]+/gi, '-')}-jobs`
            : '';
        }

        const {id, title, catId, slug} = getIdBySlug(
          'category',
          isAppHirist
            ? `${categoryInfo?.tag?.replace(' ', '-')}-jobs`
            : jobMandateInfo.jobCategory.toString(),
          tabsList,
          !isAppHirist,
          isAppHirist
        );

        const slugCategoryId = isAppHirist
          ? catId && catId > 0
            ? catId
            : id
          : jobDetails?.categoryId;

        if (slugCategoryId > -1) {
          store.dispatch({
            type: 'AppContext/set_navigation_selector',
            payload: slugCategoryId,
          });

          store.dispatch({
            type: 'AppContext/set_category_actual',
            payload: jobDetails?.categoryId,
          });

          if (isAppHirist && isBotUser) {
            const CATEGORY_MAPPED =
              HIRIST_CATEGORY_BLOG_ID_MAPPING[
                HIRIST_CATEGORY_ID_MAPPING[
                  jobDetails?.categoryId?.toString() || ''
                ] ?? jobDetails?.categoryId
              ];
            const blogs = await jobfeedService.getLatestBlogs(
              CATEGORY_MAPPED ?? '',
              headers
            );
            const bloData = (Object.values(blogs) as AdsElementType[])
              ?.map(blog => {
                return {
                  link: blog?.link,
                  title: blog?.title,
                  imageUrl: blog?.featuredImage ?? '',
                };
              })
              ?.slice(0, 3);

            store.dispatch({
              type: 'Ads/set_blog_data',
              payload: bloData,
            });
          }
        }

        store.dispatch({
          type: 'AppContext/set_slug',
          payload: title,
        });
        store.dispatch({
          type: 'AppContext/set_slug_url',
          payload: slug,
        });

        if (
          (jobDetails?.permanentlyRemoved || jobDetails?.state === 2) &&
          !jobDetails?.applied
        ) {
          if (context.res) {
            context.res.statusCode = 410; // <-- set 410 status code
          }
          return {
            props: {
              show410: true,
              jobId,
              jobMandateInfo,
              metaData,
              seoConfig: {},
              refInfo,
              isMobile,
              categoryName,
            },
          };
        }

        if (jobDetails?.hasExpired && !jobDetails?.applied) {
          return {
            redirect: {
              destination: `/c/${cleanUrl(
                categoryName
              )}?ref=${refInfo}&expJD=1`,
              permanent: true,
            },
          };
        }
      }

      jobDetails?.locations?.map((locationInfo: {id: string; name: string}) => {
        jobMandateInfo.location = `${jobMandateInfo.location} ${locationInfo?.name}`;
      });
      jobMandateInfo.exp = `(${jobDetails?.min}-${jobDetails?.max})`;
      jobMandateInfo.jobTitle = jobDetails?.title;
      jobMandateInfo.companyName = jobDetails?.companyData?.companyName || '';
    } catch (error: /* eslint-disable @typescript-eslint/no-explicit-any */ any) {
      console.log('🚀 ~ store=> ~ error:', error);
      if (
        error?.response?.data?.error?.message ===
        'Could not find job with given jobcode'
      ) {
        store.dispatch({
          type: 'job/jobdetail/set_jobdetail',
          payload: {},
        });

        return {
          props: {
            jobId,
            jobMandateInfo,
            metaData,
          },
        };
      }
      return {
        notFound: true,
      };
    }

    if (isBotUser) {
      const userIp =
        context.req?.headers?.['x-forwarded-for']
          ?.toString()
          .split(',')[0]
          .trim() ||
        context.req?.socket?.remoteAddress ||
        '';
      const headers: Record<string, any> = {
        'user-agent': context.req?.headers?.['user-agent'],
        'x-forwarded-for': userIp,
        referer: `https://www.${process.env.NEXT_PUBLIC_APP_DOMAIN}/`,
      };
      try {
        if (learnGrow?.slot) {
          const ads = await loadAds(
            learnGrow.slot,
            {
              categoryIds: jobMandateInfo.jobCategory || '',
              page: 0,
              size: 20,
              user_location: 0,
              shuffle: 1,
              orderby: 'modified',
            },
            true
          );
          if (ads) {
            store.dispatch({
              type: 'Ads/set_learn_grow',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (featuredInstitutes?.slot) {
          const ads = await loadAds(featuredInstitutes.slot, {
            categoryIds: jobMandateInfo.jobCategory || '',
          });
          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_institutes',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        try {
          /**
           * Fetch the initial state for jobs list and related ads to show
           */
          const {
            auth: {userId},
          } = store.getState();

          const jobFeeds = await jobFeedService.getSimilarJobs({
            userId,
            id: jobMandateInfo.id,
            cat: jobMandateInfo.jobCategory,
            tags: jobMandateInfo.jobTags,
            exp: getExperienceIndex(jobMandateInfo?.expMin) || undefined,
            functionalArea: jobMandateInfo?.functionalArea,
            min: jobMandateInfo?.expMin,
            size: 100,
            ...(isAppHirist && {whatFor: 'hirist'}),
            headers,
          });

          store.dispatch({
            type: 'job/jobFeed/set_jobfeed',
            payload: {totalJobs: 0, ...jobFeeds},
          });

          store.dispatch({
            type: 'job/jobFeed/set_loadMore',
            payload: false,
          });
        } catch (error) {
          console.log(
            '🚀 ~ file: [slug].tsx:200 ~ store jobdetail similar jobs=> ~ error:',
            error
          );
        }
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:277 ~ store=> ~ job details Ads API error:',
          error
        );

        return {
          notFound: true,
        };
      }
    }

    const seoConfig = await loadSEO(
      JOBDETAIL,
      getDetailSEOContent(jobMandateInfo),
      jobSlugUrl?.split('?')[0],
      true
    );

    if (!jobDetails?.active) {
      seoConfig.noindex = true;
      seoConfig.nofollow = true;
    }

    // Clear cookies if authentication failed
    if (shouldClearAuth) {
      const cookiesTodelete = getCookiesToDelete();
      context.res?.setHeader('Set-Cookie', cookiesTodelete);
    }

    return {
      props: {
        userIdSSR: userId,
        jobId,
        jobMandateInfo,
        seoConfig,
        metaData,
        refInfo,
        isMobile,
        categoryName,
        brandedJD: jobMandateInfo?.brandedJD,
        brandedS3: jobMandateInfo?.brandedUrl,
        showFreeProBannerToday: showFreeProBannerToday && isCohortEligible,
      },
    };
  }
);

export default JobDetailPage;
